import {
  assistDomainAtom,
  assistLicensesAtom,
  auditLicensesAtom,
  availableAuditEntriesAtom,
  currentAuditEntryIdAtom,
  demoAuditEntry,
  issueContentLicensesAtom,
  translateEasyLicenses,
  userAtom,
} from '@/global-store';
import { isExpired } from '@helpers/isExpired';
import { api, demoEntry } from '@http/constants';
import fetchWrapper from '@/http/fetchWrapper';
import { useURLSearchParam } from '@/hooks/useURLSearchParam';
import type {
  AuditData,
  Responsibility,
  TranslateData,
  WcagCheckerData,
  WebsiteData,
} from '@eye-able/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useRef } from 'react';

/** Which issue responsibilities (code / content / design) the org may see. */
export interface IssuesContentLicense {
  id: number;
  name: Responsibility;
}

/** Product modules the org may access, e.g. `plainLanguage`. */
export interface ModuleAccessLicense {
  id: number;
  name: string;
}

/**
 * `/listAllLicenses` returns one array per license type, always in this order.
 * `moduleAccesses` is not consumed here; the products read it via their own
 * queries.
 */
export type LicensesResponse = [
  assist: WebsiteData[],
  audit: WcagCheckerData[],
  report: AuditData[],
  translate: TranslateData[],
  issuesContent: IssuesContentLicense[],
  moduleAccesses: ModuleAccessLicense[],
];

export function useFetchLicenses() {
  const user = useAtomValue(userAtom);
  const [currentAuditEntryId, setCurrentAuditEntryId] = useAtom(
    currentAuditEntryIdAtom,
  );
  const [selectedAssistDomain, setSelectedAssistDomain] =
    useAtom(assistDomainAtom);

  const [availableAuditEntries, setAvailableAuditEntries] = useAtom(
    availableAuditEntriesAtom,
  );
  const setAuditLicenses = useSetAtom(auditLicensesAtom);
  const setAssistLicenses = useSetAtom(assistLicensesAtom);
  const setIssueContentLicenses = useSetAtom(issueContentLicensesAtom);
  const setTranslateEasyLicenses = useSetAtom(translateEasyLicenses);

  const { value: urlAuditEntryId } = useURLSearchParam('auditEntryId', {
    type: 'integer',
  });

  // Admin report-license responses are bounded, so retain an audit entry
  // selected from a deep link or persisted state when it falls outside the
  // default window. Capture the ids once so switching domains does not refetch
  // the bootstrap query.
  const isWindowedUser = Boolean(user?.isAdmin || user?.isSalesAdmin);
  const bootstrapAuditEntryIdsRef = useRef<(number | null)[] | null>(null);
  if (user && bootstrapAuditEntryIdsRef.current === null) {
    bootstrapAuditEntryIdsRef.current = isWindowedUser
      ? [urlAuditEntryId, currentAuditEntryId]
      : [];
  }
  const bootstrapAuditEntryIds = bootstrapAuditEntryIdsRef.current ?? [];

  // A refetch also asks for the current selection, which may have left the
  // window since, e.g. after unpinning. The server drops it if it was deleted.
  const currentAuditEntryIdRef = useRef(currentAuditEntryId);
  currentAuditEntryIdRef.current = currentAuditEntryId;

  const {
    data: licenses,
    isLoading,
    isError,
    error,
  } = useQuery({
    enabled: !!user,
    // Omits the current selection on purpose: keyed, every domain switch would
    // refetch the bootstrap query. The queryFn reads it at fetch time instead.
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [
      api.listAllLicenses,
      user?.id,
      user?.isAdmin,
      isWindowedUser,
      bootstrapAuditEntryIds,
    ],
    queryFn: () => {
      const requestedAuditEntryIds = isWindowedUser
        ? joinAuditEntryIds([
            ...bootstrapAuditEntryIds,
            currentAuditEntryIdRef.current,
          ])
        : '';

      return fetchWrapper.get<LicensesResponse>(
        api.listAllLicenses,
        requestedAuditEntryIds
          ? { includeAuditEntryIds: requestedAuditEntryIds }
          : undefined,
      );
    },
  });

  // Copies each license response into the global store, falls back to demo
  // data where a product has no license and keeps the selected domains valid.
  useEffect(() => {
    if (!user || isError || isLoading || !licenses) return;

    const [
      assistLicenses,
      auditLicenses,
      reportLicenses,
      translateLicenses,
      issuesContentLicenses,
    ] = licenses;

    setIssueContentLicenses(
      issuesContentLicenses.map((license) => license.name),
    );

    if (!reportLicenses.length) {
      setAvailableAuditEntries([demoAuditEntry]);
      setCurrentAuditEntryId(demoEntry.id);
    } else {
      setAvailableAuditEntries(reportLicenses);

      const hasSelectedEntry = reportLicenses.some(
        (entry) => entry.id === currentAuditEntryId,
      );

      if (!hasSelectedEntry) setCurrentAuditEntryId(reportLicenses[0].id);
    }

    const activeAssistLicenses = assistLicenses.filter(
      (license) => license.endDate !== null && !isExpired(license.endDate),
    );

    if (!activeAssistLicenses.length) {
      setAssistLicenses([]);
      setSelectedAssistDomain('demo-domain');
    } else {
      setAssistLicenses(assistLicenses);

      const hasSelectedDomain = activeAssistLicenses.some(
        (entry) => entry.domain === selectedAssistDomain,
      );

      if (!hasSelectedDomain)
        setSelectedAssistDomain(activeAssistLicenses[0].domain);
    }

    setAuditLicenses(auditLicenses);
    setTranslateEasyLicenses(translateLicenses);
    // Incomplete on purpose: with currentAuditEntryId here, every domain switch
    // would re-apply the cached response and drop entries adopted since.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    licenses,
    isLoading,
    isError,
    user,
    setAssistLicenses,
    setAuditLicenses,
    selectedAssistDomain,
    setSelectedAssistDomain,
  ]);

  // The query is disabled until a user exists, so callers must treat a missing
  // user as "not ready" themselves (BaseRoute does, via isUserReady).
  // The store is written by the effect above, one commit after the query
  // settles; `isApplied` marks the moment the entries are actually in place.
  return {
    isLoading,
    isError,
    error,
    isApplied: availableAuditEntries.length > 0,
  };
}

export function useReloadLicenseData() {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: [api.listAllLicenses],
    });
}

function joinAuditEntryIds(auditEntryIds: (number | null)[]): string {
  const presentIds = auditEntryIds.filter(
    (auditEntryId): auditEntryId is number => auditEntryId !== null,
  );
  return Array.from(new Set(presentIds)).join(',');
}
